/**
 * Green Gold Engineering — Portfolio dataset.
 *
 * 33 verified projects from the client's July 8 dispatch, grouped into 7
 * categories. Descriptions are factored into per-category shared strings
 * where the client's docx used a template; per-project descriptions are
 * inlined only when they diverge.
 *
 * Suryamanu guardrail — the source docx contained the token in the summary
 * section. Rebranded here to "Smart Water Level Controller ... — in-house
 * IoT R&D" per the site-wide rule.
 *
 * Geographic correction — the source docx said "Mohave Desert"; published
 * as "Mojave Desert" (correct US spelling). Flagged to the client.
 *
 * References-only (NOT project cards) — 10 MW ABB BESS switchboard, 2 MWh
 * Sree Umayal hybrid, and 6000A Monash Medical Centre switchgear are
 * client references shown in the summary stats band only.
 */

// ============ Shared description templates ============

const DESC_UTILITY = `Designed PV layouts, Single Line Diagrams (SLDs), DC string configurations, electrical drawings, cable schedules, and grid interconnection documentation for a utility-scale solar project. Performed energy yield assessment, equipment selection, installation supervision, quality assurance, testing, commissioning, performance validation, and grid synchronization. Successfully coordinated with utility authorities and statutory agencies for grid interconnection approvals, safety compliance, and project energization.`;

const DELIV_UTILITY = `Utility-scale design, SLDs, DC stringing, grid interconnection docs, energy yield assessment, full utility coordination`;

const DESC_FLOATING = `Designed and engineered a floating solar PV plant including PV module selection, floating platform design, mooring system design, array and string design, inverter selection and sizing, DC collection system design, AC power evacuation system, cable sizing, earthing and lightning protection system design, and preparation of Single Line Diagrams (SLD), plant layouts, and engineering documentation. Supervised installation, testing, commissioning, performance validation, and grid synchronization. Coordinated with utility authorities for grid interconnection approvals and project energization.`;

const DELIV_FLOATING = `Floating solar plant, mooring system design, SLD preparation, grid synchronization, full utility coordination`;

const DESC_ROOFTOP = `Designed and executed a grid-connected rooftop solar project including PV module selection, array and string design, mounting structure design, inverter selection and sizing, DCDB/ACDB design, cable sizing and voltage drop calculations, earthing and lightning protection system design, and grid interconnection. Supervised installation, testing, commissioning, and performance validation. Coordinated with local utility authorities for feasibility approval, safety inspection, net-metering approval, and project energization.`;

const DELIV_ROOFTOP = `Industrial rooftop, complete EPC execution, grid interconnection, net-metering compliance`;

const DESC_ROOFTOP_EDUCATIONAL = `Prepared detailed engineering design package including PV layout, module selection, array design, inverter sizing, area calculation, power output calculation, irradiance analysis, efficiency calculation, and performance optimization for an educational institution. Coordinated with local utility authorities for grid interconnection, net-metering approval, and project energization.`;

const DELIV_ROOFTOP_EDUCATIONAL = `Educational sector rooftop, complete engineering package, net-metering compliance`;

const DESC_AGRI = `Designed and engineered an agrivoltaic solar PV system combining agricultural production with solar power generation. Included PV module selection, elevated mounting structure design, array and string design, inverter selection and sizing, DCDB/ACDB design, cable sizing, earthing and lightning protection system design, and grid interconnection. Ensured optimal light transmission for crops while maximizing energy yield.`;

const DELIV_AGRI = `Agrivoltaic system, dual-use design, elevated mounting, crop optimization, grid interconnection`;

const DESC_CARPORT = `Designed and engineered a solar carport system including PV module selection, carport structure design, array and string design, inverter selection and sizing, DCDB/ACDB design, cable sizing, earthing and lightning protection system design, and grid interconnection. Integrated EV charging infrastructure with the solar carport system for comprehensive e-mobility solution.`;

const DELIV_CARPORT = `Solar carport, EV charging integration, structural design, grid interconnection`;

// ============ Categories ============

export const portfolioCategories = [
  { id: "utility-ground-mounted", label: "Utility Ground-Mounted", period: "2024–2026" },
  { id: "floating", label: "Floating Solar", period: "2024–2025" },
  { id: "hybrid-bess", label: "Hybrid Solar + BESS", period: "2024–2026" },
  { id: "industrial-rooftop", label: "Industrial & Commercial Rooftop", period: "2024–2025" },
  { id: "agrivoltaic", label: "Agrivoltaic", period: "2024–2025" },
  { id: "carport", label: "Solar Carport & Canopy", period: "2024–2025" },
  { id: "small-scale", label: "Residential, Community & Specialized", period: "2024–2025" },
];

// ============ Projects (33 verified) ============

export const portfolioProjects = [
  // ---- Utility Ground-Mounted (7) ----
  {
    id: "utility-atacama-chile-50mw",
    category: "utility-ground-mounted",
    title: "50 MW Ground-Mounted Solar PV Plant",
    location: "Atacama Desert, Chile",
    coordinates: "23.0°S",
    year: 2024,
    capacity: "50 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "50 MW", "Ground-Mounted", "Chile", "SLD", "Grid Interconnection", "Desert Climate"],
    verified: true,
  },
  {
    id: "utility-mojave-usa-75mw",
    category: "utility-ground-mounted",
    title: "75 MW Ground-Mounted Solar PV Plant",
    location: "Mojave Desert, California, USA",
    coordinates: "35.0°N",
    year: 2024,
    capacity: "75 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "75 MW", "Ground-Mounted", "USA", "SLD", "Grid Interconnection", "Desert Climate"],
    verified: true,
  },
  {
    id: "utility-tabuk-saudi-100mw",
    category: "utility-ground-mounted",
    title: "100 MW Ground-Mounted Solar PV Plant",
    location: "Tabuk Region, Saudi Arabia",
    coordinates: "28.4°N",
    year: 2025,
    capacity: "100 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "100 MW", "Ground-Mounted", "Saudi Arabia", "SLD", "Grid Interconnection", "Arid Climate"],
    verified: true,
  },
  {
    id: "utility-queensland-australia-80mw",
    category: "utility-ground-mounted",
    title: "80 MW Ground-Mounted Solar PV Plant",
    location: "Queensland Outback, Australia",
    coordinates: "24.0°S",
    year: 2025,
    capacity: "80 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "80 MW", "Ground-Mounted", "Australia", "SLD", "Grid Interconnection", "Outback Climate"],
    verified: true,
  },
  {
    id: "utility-extremadura-spain-60mw",
    category: "utility-ground-mounted",
    title: "60 MW Ground-Mounted Solar PV Plant",
    location: "Extremadura, Spain",
    coordinates: "39.0°N",
    year: 2024,
    capacity: "60 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "60 MW", "Ground-Mounted", "Spain", "SLD", "Grid Interconnection", "Mediterranean Climate"],
    verified: true,
  },
  {
    id: "utility-namib-namibia-20mw",
    category: "utility-ground-mounted",
    title: "20 MW Ground-Mounted Solar PV Plant",
    location: "Namib Desert, Namibia",
    coordinates: "24.6°S",
    year: 2025,
    capacity: "20 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "20 MW", "Ground-Mounted", "Namibia", "SLD", "Grid Interconnection", "Desert Climate"],
    verified: true,
  },
  {
    id: "utility-patagonia-argentina-15mw",
    category: "utility-ground-mounted",
    title: "15 MW Ground-Mounted Solar PV Plant",
    location: "Patagonian Plateau, Argentina",
    coordinates: "46.0°S",
    year: 2025,
    capacity: "15 MW",
    description: DESC_UTILITY,
    key_deliverables: DELIV_UTILITY,
    tags: ["Utility-Scale", "15 MW", "Ground-Mounted", "Argentina", "SLD", "Grid Interconnection", "Semi-Arid Climate"],
    verified: true,
  },

  // ---- Floating Solar (2) ----
  {
    id: "floating-portugal-25mw",
    category: "floating",
    title: "25 MW Floating Solar PV Plant",
    location: "Hydropower Reservoir, Portugal",
    coordinates: "39.5°N",
    year: 2025,
    capacity: "25 MW",
    description: DESC_FLOATING,
    key_deliverables: DELIV_FLOATING,
    tags: ["Utility-Scale", "25 MW", "Floating Solar", "Portugal", "SLD", "Grid Interconnection", "Reservoir"],
    verified: true,
  },
  {
    id: "floating-thailand-12mw",
    category: "floating",
    title: "12 MW Floating Solar PV Plant",
    location: "Irrigation Reservoir, Thailand",
    coordinates: "15.9°N",
    year: 2025,
    capacity: "12 MW",
    description: DESC_FLOATING,
    key_deliverables: DELIV_FLOATING,
    tags: ["Utility-Scale", "12 MW", "Floating Solar", "Thailand", "SLD", "Grid Interconnection", "Tropical Climate"],
    verified: true,
  },

  // ---- Hybrid Solar + BESS (4) ----
  {
    id: "hybrid-texas-usa-10mw",
    category: "hybrid-bess",
    title: "10 MW Solar + 5 MWh BESS Hybrid Plant",
    location: "Grid Stabilization Site, Texas, USA",
    coordinates: "32.6°N",
    year: 2025,
    capacity: "10 MW + 5 MWh",
    description: `Designed and executed a hybrid solar PV and Battery Energy Storage System (BESS) for grid stabilization applications. Responsibilities included PV module selection, array and string design, BESS sizing and configuration, Battery Management System (BMS) integration, Power Conversion System (PCS) sizing, DCDB/ACDB design, cable sizing, protection coordination, energy management strategy, and grid interconnection design. Supervised installation, testing, commissioning, and performance validation. Coordinated with utility authorities for grid support compliance and project energization.`,
    key_deliverables: `Hybrid solar + BESS, BMS/PCS integration, grid stabilization, energy management strategy`,
    tags: ["Hybrid", "BESS", "10 MW", "Texas", "Grid Stabilization", "Battery Storage", "Utility-Scale"],
    verified: true,
  },
  {
    id: "hybrid-western-australia-5mw",
    category: "hybrid-bess",
    title: "5 MW Solar + 2.5 MWh BESS Hybrid Plant",
    location: "Mining Operation, Western Australia",
    coordinates: "27.0°S",
    year: 2025,
    capacity: "5 MW + 2.5 MWh",
    description: `Designed and executed a hybrid solar PV and Battery Energy Storage System (BESS) for industrial mining operation. Included PV module selection, array design, BESS sizing, BMS/PCS integration, power distribution design, cable sizing, protection coordination, and grid interconnection. Supervised installation, testing, commissioning, and performance validation for reliable industrial power supply.`,
    key_deliverables: `Hybrid solar + BESS, mining operation, BMS/PCS integration, industrial power supply`,
    tags: ["Hybrid", "BESS", "5 MW", "Australia", "Mining", "Battery Storage", "Industrial"],
    verified: true,
  },
  {
    id: "hybrid-crete-greece-2mw",
    category: "hybrid-bess",
    title: "2 MW Solar + 1 MWh BESS Hybrid Plant",
    location: "Island Microgrid, Crete, Greece",
    coordinates: "35.3°N",
    year: 2024,
    capacity: "2 MW + 1 MWh",
    description: `Designed and executed a hybrid solar PV and Battery Energy Storage System (BESS) for island microgrid application. Included PV module selection, array design, BESS sizing, BMS/PCS integration, energy management strategy, and grid interconnection design for autonomous island power system.`,
    key_deliverables: `Hybrid solar + BESS, island microgrid, energy management, autonomous power system`,
    tags: ["Hybrid", "BESS", "2 MW", "Greece", "Island Microgrid", "Battery Storage", "Mediterranean"],
    verified: true,
  },
  {
    id: "hybrid-greenland-500kw",
    category: "hybrid-bess",
    title: "500 kW Solar + 250 kWh BESS Hybrid Plant",
    location: "Remote Research Facility, Greenland",
    coordinates: "71.7°N",
    year: 2025,
    capacity: "500 kW + 250 kWh",
    description: `Designed and executed a hybrid solar PV and Battery Energy Storage System (BESS) for remote arctic research facility. Included PV module selection optimized for low-irradiance conditions, array design, BESS sizing, BMS/PCS integration, thermal management, and grid interconnection design for extreme climate operation.`,
    key_deliverables: `Hybrid solar + BESS, arctic research facility, low-irradiance optimization, thermal management`,
    tags: ["Hybrid", "BESS", "500 kW", "Greenland", "Remote", "Battery Storage", "Arctic Climate"],
    verified: true,
  },

  // ---- Industrial & Commercial Rooftop (8) ----
  {
    id: "rooftop-rotterdam-netherlands-5mw",
    category: "industrial-rooftop",
    title: "5 MW On-Grid Rooftop Solar",
    location: "Industrial Park, Rotterdam, Netherlands",
    coordinates: "51.9°N",
    year: 2024,
    capacity: "5 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "5 MW", "Netherlands", "Industrial", "Commercial", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-stuttgart-germany-3mw",
    category: "industrial-rooftop",
    title: "3 MW On-Grid Rooftop Solar",
    location: "Manufacturing Facility, Stuttgart, Germany",
    coordinates: "48.8°N",
    year: 2024,
    capacity: "3 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "3 MW", "Germany", "Industrial", "Commercial", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-dubai-uae-4mw",
    category: "industrial-rooftop",
    title: "4 MW On-Grid Rooftop Solar",
    location: "Logistics Hub, Dubai, UAE",
    coordinates: "25.2°N",
    year: 2025,
    capacity: "4 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "4 MW", "UAE", "Commercial", "Dubai", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-sao-paulo-brazil-2-5mw",
    category: "industrial-rooftop",
    title: "2.5 MW On-Grid Rooftop Solar",
    location: "Textile Complex, São Paulo, Brazil",
    coordinates: "23.5°S",
    year: 2025,
    capacity: "2.5 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "2.5 MW", "Brazil", "Industrial", "Commercial", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-detroit-usa-1-5mw",
    category: "industrial-rooftop",
    title: "1.5 MW On-Grid Rooftop Solar",
    location: "Automotive Plant, Detroit, Michigan, USA",
    coordinates: "42.3°N",
    year: 2024,
    capacity: "1.5 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "1.5 MW", "USA", "Industrial", "Automotive", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-buenos-aires-argentina-2mw",
    category: "industrial-rooftop",
    title: "2 MW On-Grid Rooftop Solar",
    location: "Food Processing Facility, Buenos Aires, Argentina",
    coordinates: "34.6°S",
    year: 2025,
    capacity: "2 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "2 MW", "Argentina", "Industrial", "Food Processing", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-istanbul-turkey-1mw",
    category: "industrial-rooftop",
    title: "1 MW On-Grid Rooftop Solar",
    location: "Shopping Mall Complex, Istanbul, Turkey",
    coordinates: "41.0°N",
    year: 2024,
    capacity: "1 MW",
    description: DESC_ROOFTOP,
    key_deliverables: DELIV_ROOFTOP,
    tags: ["Rooftop", "On-Grid", "1 MW", "Turkey", "Commercial", "Retail", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "rooftop-toronto-canada-800kw",
    category: "industrial-rooftop",
    title: "800 kW On-Grid Rooftop Solar",
    location: "University Campus, Toronto, Canada",
    coordinates: "43.7°N",
    year: 2025,
    capacity: "800 kW",
    description: DESC_ROOFTOP_EDUCATIONAL,
    key_deliverables: DELIV_ROOFTOP_EDUCATIONAL,
    tags: ["Rooftop", "On-Grid", "800 kW", "Canada", "Educational", "University", "Grid Interconnection"],
    verified: true,
  },

  // ---- Agrivoltaic (2) ----
  {
    id: "agri-provence-france-5mw",
    category: "agrivoltaic",
    title: "5 MW Agrivoltaic Farm Array",
    location: "Agricultural Region, Provence, France",
    coordinates: "43.9°N",
    year: 2025,
    capacity: "5 MW",
    description: DESC_AGRI,
    key_deliverables: DELIV_AGRI,
    tags: ["Agrivoltaic", "5 MW", "France", "Agricultural", "Dual-Use", "SLD", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "agri-napa-usa-3mw",
    category: "agrivoltaic",
    title: "3 MW Agrivoltaic Farm Array",
    location: "Vineyard Region, Napa Valley, California, USA",
    coordinates: "38.3°N",
    year: 2024,
    capacity: "3 MW",
    description: DESC_AGRI,
    key_deliverables: DELIV_AGRI,
    tags: ["Agrivoltaic", "3 MW", "USA", "California", "Vineyard", "Dual-Use", "SLD", "Grid Interconnection"],
    verified: true,
  },

  // ---- Solar Carport & Canopy (2) ----
  {
    id: "carport-abu-dhabi-uae-1-5mw",
    category: "carport",
    title: "1.5 MW Solar Carport with EV Charging",
    location: "International Airport Parking, Abu Dhabi, UAE",
    coordinates: "24.4°N",
    year: 2025,
    capacity: "1.5 MW",
    description: DESC_CARPORT,
    key_deliverables: DELIV_CARPORT,
    tags: ["Carport", "1.5 MW", "UAE", "Abu Dhabi", "Airport", "Commercial", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "carport-seoul-south-korea-800kw",
    category: "carport",
    title: "800 kW Solar Carport with EV Charging",
    location: "Corporate Campus Parking, Seoul, South Korea",
    coordinates: "37.6°N",
    year: 2024,
    capacity: "800 kW",
    description: DESC_CARPORT,
    key_deliverables: DELIV_CARPORT,
    tags: ["Carport", "800 kW", "South Korea", "Seoul", "Corporate", "Commercial", "Grid Interconnection"],
    verified: true,
  },

  // ---- Residential, Community & Specialized (8) ----
  {
    id: "small-nairobi-kenya-50kw",
    category: "small-scale",
    title: "50 kW On-Grid Rooftop Solar",
    location: "Community Center, Nairobi, Kenya",
    coordinates: "1.3°S",
    year: 2024,
    capacity: "50 kW",
    description: `Designed and executed a grid-connected rooftop solar project for community facility including complete engineering documentation, installation supervision, testing, and commissioning. Coordinated with local utility authorities for all statutory approvals including feasibility, safety inspection, net-metering, and energization.`,
    key_deliverables: `Community facility rooftop, full statutory coordination, net-metering`,
    tags: ["Rooftop", "On-Grid", "50 kW", "Community", "Kenya", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "small-tuscany-italy-100kw",
    category: "small-scale",
    title: "100 kW On-Grid Rooftop Solar",
    location: "Agricultural Cooperative, Tuscany, Italy",
    coordinates: "43.4°N",
    year: 2024,
    capacity: "100 kW",
    description: `Designed and executed a grid-connected rooftop solar project for agricultural cooperative including PV module selection, array design, inverter sizing, area and power output calculations, irradiance analysis, and performance optimization. Coordinated with local utility authorities for grid interconnection and net-metering approval.`,
    key_deliverables: `Agricultural cooperative rooftop, performance optimization, net-metering`,
    tags: ["Rooftop", "On-Grid", "100 kW", "Agricultural", "Italy", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "small-ho-chi-minh-vietnam-75kw",
    category: "small-scale",
    title: "75 kW On-Grid Rooftop Solar",
    location: "Hospital Complex, Ho Chi Minh City, Vietnam",
    coordinates: "10.8°N",
    year: 2025,
    capacity: "75 kW",
    description: `Designed and executed a grid-connected rooftop solar project for healthcare facility including complete engineering, installation, testing, and commissioning. Coordinated with local utility authorities for all statutory approvals including feasibility, safety inspection, net-metering, and energization.`,
    key_deliverables: `Healthcare facility rooftop, full statutory coordination, net-metering`,
    tags: ["Rooftop", "On-Grid", "75 kW", "Healthcare", "Vietnam", "Hospital", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "small-mexico-city-mexico-200kw",
    category: "small-scale",
    title: "200 kW On-Grid Rooftop Solar",
    location: "School District, Mexico City, Mexico",
    coordinates: "19.4°N",
    year: 2025,
    capacity: "200 kW",
    description: DESC_ROOFTOP_EDUCATIONAL,
    key_deliverables: DELIV_ROOFTOP_EDUCATIONAL,
    tags: ["Rooftop", "On-Grid", "200 kW", "Educational", "Mexico", "School", "Grid Interconnection"],
    verified: true,
  },
  {
    id: "small-svalbard-norway-30kw",
    category: "small-scale",
    title: "30 kW Hybrid Solar + Battery Storage",
    location: "Research Station, Svalbard, Norway",
    coordinates: "78.2°N",
    year: 2025,
    capacity: "30 kW",
    description: `Designed hybrid solar PV system with battery storage integration. Responsible for complete system design including PV layout, module selection, array design, inverter sizing, battery sizing & calculation, and performance optimization. Ensured reliable power supply for remote application.`,
    key_deliverables: `Hybrid system with battery backup, Battery, performance optimization`,
    tags: ["Hybrid", "Battery Storage", "30 kW", "Arctic", "Norway", "Research Station", "Inverter Sizing"],
    verified: true,
  },
  {
    id: "small-singapore-40kw",
    category: "small-scale",
    title: "40 kW Façade-Integrated Solar (BIPV)",
    location: "Commercial Tower, Singapore",
    coordinates: "1.3°N",
    year: 2024,
    capacity: "40 kW",
    description: `Designed façade-integrated on-grid solar PV system including PV layout, module selection, array design, inverter sizing, area and power output calculations, irradiance analysis, efficiency optimization, and performance modeling.`,
    key_deliverables: `Façade-integrated BIPV, on-grid design, performance optimization`,
    tags: ["On-Grid", "40 kW", "BIPV", "Façade Integration", "Singapore", "Performance Modeling", "Tropical Climate"],
    verified: true,
  },
  {
    id: "small-maldives-25kw",
    category: "small-scale",
    title: "25 kW Off-Grid Solar + 20 kWh Battery",
    location: "Island Resort, Maldives",
    coordinates: "4.2°N",
    year: 2025,
    capacity: "25 kW",
    description: `Designed standalone solar power system for remote island application. Included PV array sizing, battery bank design, module selection, inverter sizing, area and power calculations, irradiance analysis, and performance optimization for off-grid operation.`,
    key_deliverables: `Standalone off-grid system, island application, battery bank design`,
    tags: ["Off-Grid", "25 kW", "Standalone", "Battery Bank", "Maldives", "Island Resort", "Tropical Climate"],
    verified: true,
  },
  {
    id: "small-copenhagen-denmark-60kw",
    category: "small-scale",
    title: "60 kW On-Grid Rooftop Solar",
    location: "Municipal Building, Copenhagen, Denmark",
    coordinates: "55.7°N",
    year: 2024,
    capacity: "60 kW",
    description: `Designed and executed a grid-connected rooftop solar project for municipal building including complete engineering documentation, installation supervision, testing, and commissioning. Coordinated with local utility authorities for all statutory approvals.`,
    key_deliverables: `Municipal building rooftop, statutory coordination, net-metering`,
    tags: ["Rooftop", "On-Grid", "60 kW", "Municipal", "Denmark", "Grid Interconnection", "Nordic Climate"],
    verified: true,
  },
];

// ============ Summary stats ============

export const portfolioSummary = {
  total_projects: "50+",
  solar_capacity_designed: "~500 MWp",
  solar_breakdown: "rooftop + ground-mounted + floating + agrivoltaic + carport",
  largest_single_project: "100 MW Ground-Mounted, Tabuk Region, Saudi Arabia",
  bess_capacity_designed: "12 MWh+",
  bess_breakdown:
    "including 10 MW BESS for ABB, 2 MWh hybrid for Sree Umayal, plus 8.75 MWh new international BESS",
  switchgear_max_rating: "6000A LV Switchgear (Monash Medical Centre)",
  bess_switchboards_abb: "10 × 1 MW + 1 × 10 MW BESS switchboard designs",
  countries_served: [
    "UK", "USA", "Australia", "Spain", "South Africa", "Ethiopia", "India",
    "Chile", "Saudi Arabia", "Netherlands", "Germany", "UAE", "Brazil",
    "Argentina", "Turkey", "Canada", "South Korea", "Kenya", "Italy",
    "Vietnam", "Denmark", "Singapore", "Maldives", "Norway", "Portugal",
    "Thailand", "Namibia", "France", "Greece", "Greenland", "Mexico",
  ],
  indian_statutory_approvals: "TNEB / TANGEDCO / CEIG — 25+ projects",
  iot_products_developed:
    "Smart Water Level Controller (Android + Wi-Fi + Cloud) — in-house IoT R&D",
  battery_cell_rd: "Prismatic LFP — full manufacturing process development",
  ev_charging_rd: "AC/DC charger architecture, power electronics, grid integration",
};
